import { useMemo, useRef } from "react";
import { useUserContext } from "@hooks/use-auth-context";

export type CapabilityRequirement = string | string[];

const matches = (
  caps: Record<string, boolean>,
  required: CapabilityRequirement,
  requireAll: boolean
): boolean => {
  const list = Array.isArray(required) ? required : [required];
  if (list.length === 0) return true;
  return requireAll
    ? list.every((cap) => caps[cap] === true)
    : list.some((cap) => caps[cap] === true);
};

/**
 * Capability lookup helpers.
 * Reads from the dedicated `capabilities` context state first (always set by
 * fetchMe), then falls back to `user.capabilities`. Prefer whichever is populated
 * so we never miss caps during the brief restore window where one slice updates
 * before the other.
 *
 * The `stableRef` ensures we never regress: if the incoming caps are empty
 * (e.g. during a token refresh before fetchMe resolves), we keep the last
 * known non-empty set rather than flashing back to a stale/restricted view.
 */
export const useCapabilities = () => {
  const { user, capabilities } = useUserContext();
  const fromContext = capabilities ?? {};
  const fromUser = user?.capabilities ?? {};
  const incoming =
    Object.keys(fromContext).length > 0 ? fromContext : fromUser;

  // Only advance forward — never regress to empty/stale during a refresh.
  const stableRef = useRef<Record<string, boolean>>(incoming);
  if (Object.keys(incoming).length > 0) {
    stableRef.current = incoming;
  }
  const caps = stableRef.current;

  return useMemo(
    () => ({
      caps,
      can: (cap: string) => caps[cap] === true,
      hasAny: (required: CapabilityRequirement) =>
        matches(caps, required, false),
      hasAll: (required: CapabilityRequirement) =>
        matches(caps, required, true),
    }),
    [caps]
  );
};
